/**
 * MBA Lab - account sync for the /tools pages.
 *
 * Signed-in visitors get their saved tool state on every device they use. Anonymous visitors get
 * exactly what they always got: state in this browser, and not one network request.
 *
 * DELIBERATELY STANDALONE - no import from src/app/shared. Two reasons, both real:
 *   1. webpack's `appShared` cache group extracts anything under src/app/shared into
 *      app-shared.bundle.js, which /tools pages never load. The import would vanish at runtime.
 *   2. That client redirects to sign-in on a 401. Correct for a hub page a person is looking at;
 *      wrong for a background autosave, which must fail quietly and leave the tool working.
 *
 * The protocol, in one place:
 *   - Ordering is by the client's own `savedAt` (epoch ms), which every tool model already writes.
 *     The server applies a save only when it is strictly newer than what it holds, so a delayed
 *     request from a second device cannot move the account backwards.
 *   - Deleting writes a tombstone rather than removing the row, so a device still holding an older
 *     copy cannot re-upload it and resurrect what was just cleared.
 *   - Local state and account state live under different storage keys (see core.js's storageKey),
 *     so signing out never leaves your work for the next person at this computer.
 */

import { anonymousStore, hasAccountNamespace, store } from './core.js';

/**
 * The row in the page hero that everything this module renders shares with tool-pin.js's
 * "Pin to sidebar" button. Find-or-create, because the two live in separate bundles and neither
 * can assume it ran first - and it is created here rather than in Razor so neither has to be
 * placed correctly in fifteen hand-written heroes.
 *
 * Kept in lockstep with the identical helper in tool-pin.js: same id, same parent, same fallback.
 */
function heroActionsRow() {
  const existing = document.getElementById('toolHeroActions');
  if (existing) return existing;

  const parent = document.querySelector('.page-hero .container') || document.querySelector('.app-main');
  if (!parent) return null;

  const row = document.createElement('div');
  row.id = 'toolHeroActions';
  row.className = 'tool-hero-actions';
  parent.appendChild(row);
  return row;
}

/** How long a hydrate GET may hold up the tool's own startup. Past this the tool opens on local
  * state and syncs on the next save - never a blank page waiting on a network. */
const HYDRATE_TIMEOUT_MS = 1500;

/** Autosave debounce. Long enough to coalesce a burst of typing, short enough that closing the tab
  * a second later rarely loses anything. */
const SAVE_DEBOUNCE_MS = 1200;

/** Hard ceiling between saves while someone types continuously - without it, an uninterrupted
  * editing session would never reach a quiet moment and would never save at all. */
const SAVE_MAX_WAIT_MS = 10000;

const RETRY_BASE_MS = 2000;
const MAX_RETRIES = 3;

/** Every state of the little "Saved to your account" line. The tool renders these; this module
  * only ever reports which one is true, and never claims the account has something it does not. */
export const SyncStatus = Object.freeze({
  Idle: 'idle',
  Saving: 'saving',
  SavedToAccount: 'saved-to-account',
  SavedOnDevice: 'saved-on-device',
  Unavailable: 'unavailable',
});

function endpoint(slug) {
  return `/api/hub/tools/state/${encodeURIComponent(slug)}`;
}

function csrfToken() {
  try {
    return (window.__config && window.__config.csrfToken) || '';
  } catch {
    return '';
  }
}

/**
 * True when this page may sync at all: rendered for a signed-in visitor AND the feature is on.
 * Both come from _AppConfig, which is only emitted on an authenticated response - so an anonymous
 * page cannot reach any of the code below.
 */
export function canSync() {
  try {
    return hasAccountNamespace() && window.__config.toolSyncEnabled === true;
  } catch {
    return false;
  }
}

async function requestJson(url, options) {
  const response = await fetch(url, {
    credentials: 'same-origin',
    ...options,
    headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrfToken(), ...(options && options.headers) },
  });

  if (!response.ok) {
    const error = new Error(`Tool sync request failed (${response.status}).`);
    error.status = response.status;
    throw error;
  }

  return response.json();
}

/** A failure that means "stop trying on this page" rather than "try again in a moment". A rejected
  * payload or a lost session will not fix itself by being retried; a timeout or a 500 might. */
function isPermanent(status) {
  return status === 401 || status === 403 || status === 404 || (status >= 400 && status < 500 && status !== 429);
}

/**
 * Reads the account's copy and reconciles it with what this browser holds, BEFORE the tool
 * initializes - so the tool then loads its own state exactly as it always has and needs no
 * knowledge of any of this.
 *
 * Four outcomes, and the two easy ones to get wrong are the last two:
 *   - the account is newer  -> write it into local storage; the tool opens on it
 *   - local is newer, or the account has nothing -> UPLOAD it. Leaving it local-only would keep a
 *     stale server copy authoritative for every other device the person owns.
 *   - the account was cleared elsewhere more recently -> clear local too, or "Start over" on one
 *     device silently un-does itself on the next
 *   - the account holds a NEWER SCHEMA than this build understands -> touch nothing and stop
 *     syncing. An old client cannot represent fields it has never heard of, so writing would
 *     silently drop them. Mirrors the tool models' own `unsupported-newer` rule.
 *
 * @param {string} slug - registry slug, also the storage key and the API path segment.
 * @param {{load: Function, save: Function, clear: Function}} localStore - the tool's own store.
 * @param {number} schemaVersion - the tool model's SCHEMA_VERSION.
 * @returns {Promise<{synced: boolean, reason?: string}>}
 */
export async function hydrate(slug, localStore, schemaVersion) {
  if (!canSync()) return { synced: false, reason: 'not-signed-in' };

  let remote;
  try {
    remote = await requestJson(endpoint(slug), { signal: AbortSignal.timeout(HYDRATE_TIMEOUT_MS) });
  } catch {
    // Offline, slow, signed out, feature off - all the same from here: the tool opens on whatever
    // this browser has, which is what it would have done anyway.
    return { synced: false, reason: 'unreachable' };
  }

  const local = localStore.load();
  const localSavedAt = Number(local && local.savedAt) || 0;

  if (remote.found) {
    if (Number(remote.schemaVersion) > schemaVersion) {
      return { synced: false, reason: 'newer-schema' };
    }
    if (Number(remote.savedAtClientMs) > localSavedAt) {
      localStore.save(remote.state);
      return { synced: true, reason: 'account-newer' };
    }
    // STRICTLY newer, not merely present: equality is the steady state after every synced save
    // (push sends state.savedAt verbatim, so a reload compares equal), and uploading it anyway
    // would add a full-state PUT - awaited, before the tool constructs - to virtually every
    // repeat visit, for the server to answer accepted:false.
    if (local && localSavedAt > Number(remote.savedAtClientMs)) {
      await push(slug, local, schemaVersion, HYDRATE_TIMEOUT_MS).catch(() => {});
      return { synced: true, reason: 'local-newer' };
    }
    return { synced: true, reason: 'in-step' };
  }

  const deletedAt = Number(remote.deletedAtClientMs) || 0;
  if (deletedAt > 0 && deletedAt > localSavedAt) {
    localStore.clear();
    return { synced: true, reason: 'cleared-elsewhere' };
  }

  if (local) {
    await push(slug, local, schemaVersion, HYDRATE_TIMEOUT_MS).catch(() => {});
    return { synced: true, reason: 'uploaded-local' };
  }

  // Nothing in the account and nothing under this person's own key - the one moment a draft they
  // started before signing in is worth offering. Deliberately not awaited: it renders a prompt and
  // waits on a human, and the tool must not sit unbuilt behind that.
  offerAnonymousDraft(slug, localStore, schemaVersion);
  return { synced: true, reason: 'nothing-anywhere' };
}

/**
 * One PUT. Resolves with the server's verdict - `accepted` is false when a newer save from
 * another device already won, which the caller must not report as "saved to your account".
 *
 * `timeoutMs` is not optional in spirit: hydrate awaits this BEFORE the tool is constructed, so an
 * unbounded push on a stalled connection (a captive portal, a black-holed TCP connection) leaves
 * the page rendered and completely inert - every field and button unwired, with no error. The
 * background autosave passes no timeout because nothing is waiting on it.
 */
async function push(slug, state, schemaVersion, timeoutMs) {
  return requestJson(endpoint(slug), {
    method: 'PUT',
    body: JSON.stringify({
      state,
      savedAtClientMs: Number(state && state.savedAt) || Date.now(),
      schemaVersion,
    }),
    ...(timeoutMs ? { signal: AbortSignal.timeout(timeoutMs) } : {}),
  });
}

/**
 * Wraps a tool's store so saving also syncs. Same load/save/clear surface, so a tool swaps one for
 * the other and changes nothing else.
 *
 * `clear()` is deliberately LOCAL ONLY. Tools call it for their own reasons, and a wrapper that
 * quietly turned every one of those into "delete this from my account, everywhere" would be a
 * destructive action nobody asked for. Removing the account's copy is `deleteAccountState`, wired
 * at the call sites that actually mean it: Start over, and turning a tiered tool's "remember" off.
 *
 * @param {string} slug
 * @param {{load: Function, save: Function, clear: Function}} localStore
 * @param {{schemaVersion?: number, onStatus?: (status: string) => void}} [options]
 */
export function syncedStore(slug, localStore, options = {}) {
  const schemaVersion = options.schemaVersion || 1;
  const onStatus = typeof options.onStatus === 'function' ? options.onStatus : () => {};

  let timer = null;
  let firstPendingAt = 0;
  let pending = null;
  // Bumped whenever this save is SUPERSEDED - by a clear, a delete, or a NEWER save being
  // scheduled. A response carrying a stale generation is dropped entirely: it may not touch the
  // indicator (a slow older PUT resolving after a newer one would flip "Saved to your account"
  // back to something staler) and its failure path may not re-queue the old state.
  let generation = 0;
  let disabled = !canSync();
  let retries = 0;

  function cancelPending() {
    if (timer) clearTimeout(timer);
    timer = null;
    pending = null;
    firstPendingAt = 0;
    generation += 1;
  }

  async function flush() {
    const mine = generation;
    const state = pending;
    timer = null;
    pending = null;
    firstPendingAt = 0;
    if (!state || disabled) return;

    onStatus(SyncStatus.Saving);
    try {
      const result = await push(slug, state, schemaVersion);
      if (mine !== generation) return;
      retries = 0;
      // accepted:false means a newer save from elsewhere is the account's copy. The work is still
      // safe in this browser, so say that rather than claiming an account save that did not happen.
      onStatus(result.accepted ? SyncStatus.SavedToAccount : SyncStatus.SavedOnDevice);
    } catch (err) {
      if (mine !== generation) return;

      if (isPermanent(err.status)) {
        disabled = true;
        onStatus(SyncStatus.Unavailable);
        return;
      }

      if (retries < MAX_RETRIES) {
        retries += 1;
        // Re-queue through schedule(), NOT by assigning `pending` and arming a second timer.
        // Doing it by hand loses work twice over: it overwrites anything saved WHILE this request
        // was in flight (the newer state silently disappears and the account keeps the older one),
        // and it leaves that newer save's own timer armed and unreachable. schedule() clears the
        // existing timer and keeps whichever state is actually newer.
        const newest = !pending || (Number(pending.savedAt) || 0) < (Number(state.savedAt) || 0) ? state : pending;
        schedule(newest, RETRY_BASE_MS * retries);
        onStatus(SyncStatus.SavedOnDevice);
        return;
      }

      // Out of retries. Park the state as pending WITHOUT a timer - that is the exact shape the
      // `online` listener below looks for, so reconnecting resends it instead of the work waiting
      // for the next edit or the next visit's hydrate. Nulling it here would make that listener
      // unsatisfiable, which is precisely the dead-code bug this used to have.
      retries = 0;
      pending = state;
      firstPendingAt = 0;
      onStatus(SyncStatus.SavedOnDevice);
    }
  }

  /**
   * The single place a save is queued. Always clears any timer already armed, so there is only
   * ever one - the retry path re-enters here rather than arming a second one of its own.
   *
   * @param {object} state
   * @param {number} [delayMs] - retry backoff. Without it, the normal debounce applies.
   */
  function schedule(state, delayMs) {
    // Scheduling supersedes whatever is in flight: from this moment, only THIS save may report
    // status or retry. Without the bump, a PUT slower than the debounce overlaps the next one,
    // the two race, and whichever resolves LAST owns the indicator - usually the older request,
    // flipping "Saved to your account" back to something staler while its failure path re-queues
    // state the account has already moved past.
    generation += 1;
    pending = state;
    if (!firstPendingAt) firstPendingAt = Date.now();

    if (timer) clearTimeout(timer);
    // Someone typing without pause would otherwise keep pushing the debounce out forever.
    const waited = Date.now() - firstPendingAt;
    const debounced = waited >= SAVE_MAX_WAIT_MS ? 0 : Math.min(SAVE_DEBOUNCE_MS, SAVE_MAX_WAIT_MS - waited);
    timer = setTimeout(flush, delayMs === undefined ? debounced : delayMs);
  }

  // A save that lands while offline should go out as soon as the connection is back, rather than
  // waiting for the person to touch the form again.
  if (!disabled && typeof window !== 'undefined') {
    window.addEventListener('online', () => {
      if (pending && !timer) schedule(pending);
    });
  }

  return {
    load: () => localStore.load(),

    save(value) {
      // core.js's wrapper returns false rather than throwing when storage is full or blocked
      // (private-mode Safari, quota). Syncing still goes ahead - the account copy is exactly what
      // makes that survivable - but the indicator must not then say "Saved on this device", which
      // would be the one wrong thing to tell someone whose browser just refused to keep their work.
      const ok = localStore.save(value);
      if (!disabled) {
        schedule(value);
      } else {
        onStatus(ok ? SyncStatus.SavedOnDevice : SyncStatus.Unavailable);
      }
      return ok;
    },

    clear() {
      // Local only - see this function's own doc comment.
      cancelPending();
      localStore.clear();
    },

    /** Removes the account's copy as well: "Start over", or a tiered tool's remember-off. */
    async deleteAccountState() {
      cancelPending();
      localStore.clear();
      if (disabled) return;

      try {
        await requestJson(`${endpoint(slug)}?deletedAtClientMs=${Date.now()}`, { method: 'DELETE' });
        onStatus(SyncStatus.Idle);
      } catch (err) {
        if (isPermanent(err.status)) disabled = true;
        // A failed delete is not worth blocking the reset the user asked for. Their local copy is
        // gone either way, and the account copy is superseded by the next save.
      }
    },

    /** True while this page is still willing to sync - false once a permanent failure closed it. */
    get isSyncing() {
      return !disabled;
    },
  };
}

const STATUS_TEXT = Object.freeze({
  [SyncStatus.Saving]: 'Saving…',
  [SyncStatus.SavedToAccount]: 'Saved to your account',
  [SyncStatus.SavedOnDevice]: 'Saved on this device',
  [SyncStatus.Unavailable]: 'Account saving unavailable',
});

// Free-tier Font Awesome only - fa-cloud-check and fa-cloud-slash are Pro icons, and a Pro name
// in the free set renders as an empty glyph gap right where the reassurance should be.
const STATUS_ICON = Object.freeze({
  [SyncStatus.Saving]: 'fa-arrows-rotate',
  [SyncStatus.SavedToAccount]: 'fa-cloud',
  [SyncStatus.SavedOnDevice]: 'fa-laptop',
  [SyncStatus.Unavailable]: 'fa-triangle-exclamation',
});

/**
 * Builds the one-line "Saved to your account" indicator and returns the onStatus callback that
 * drives it. Created here rather than as markup in each of the twelve tool views: the wording is
 * a promise about where someone's work actually is, and twelve copies of a promise drift.
 *
 * Renders nothing at all when this page cannot sync, so an anonymous tool page is untouched.
 *
 * @returns {(status: string) => void}
 */
export function mountSyncIndicator() {
  if (!canSync() || typeof document === 'undefined') return () => {};

  // Inside the hero, where the page already says what it is - not floating over the content, which
  // on a phone would land on top of the tool dock.
  const host = heroActionsRow();
  if (!host) return () => {};

  const el = document.createElement('p');
  el.className = 'tool-sync-status';
  // Polite, not assertive: this is reassurance, and it changes on every keystroke burst. Nobody
  // should have their screen reader interrupted to be told their work saved.
  el.setAttribute('aria-live', 'polite');
  el.hidden = true;
  host.appendChild(el);

  return (status) => {
    const text = STATUS_TEXT[status];
    if (!text) {
      el.hidden = true;
      el.textContent = '';
      return;
    }
    el.hidden = false;
    el.innerHTML = '';
    const icon = document.createElement('i');
    icon.className = `fa-solid ${STATUS_ICON[status]}`;
    icon.setAttribute('aria-hidden', 'true');
    el.appendChild(icon);
    el.appendChild(document.createTextNode(` ${text}`));
    el.dataset.status = status;
  };
}

/**
 * Whether to offer to bring a draft this person started before signing in into their account.
 *
 * Deliberately an OFFER, never automatic. The anonymous draft may not be theirs - a shared library
 * machine, a partner's laptop - and silently uploading whatever the last person left in the tool
 * would be exactly the kind of thing this whole design is built to avoid.
 *
 * @returns {{available: boolean, draft: object|null}}
 */
export function anonymousDraftToOffer(slug, localStore) {
  if (!canSync()) return { available: false, draft: null };
  // Only when the account side is genuinely empty - never a prompt that could overwrite real work.
  if (localStore.load()) return { available: false, draft: null };
  // And never again once they have said no, for this tool on this account.
  if (declineStore(slug).load() === true) return { available: false, draft: null };

  const draft = anonymousStore(slug).load();
  return draft ? { available: true, draft } : { available: false, draft: null };
}

/** Remembers a "no thanks" per tool, in the ACCOUNT namespace - so declining does not decide for
  * the next person to sign in on this computer. */
function declineStore(slug) {
  return store(`${slug}-import-declined`);
}

/**
 * Renders the offer, if there is one to make. Closes the gap where someone uses a tool, likes it,
 * signs up, and finds their work gone - which is the worst possible first impression of an account.
 *
 * Mounted here rather than per tool because every syncable tool has the same gap and none of them
 * should have to know about it. Accepting reloads: the tool read its storage at construction, and
 * a reload is a far smaller thing than a re-entry path in twelve modules.
 */
function offerAnonymousDraft(slug, localStore, schemaVersion) {
  const { available, draft } = anonymousDraftToOffer(slug, localStore);
  if (!available || typeof document === 'undefined') return;

  const host = heroActionsRow();
  if (!host) return;

  const wrap = document.createElement('p');
  wrap.className = 'tool-sync-offer';

  const text = document.createElement('span');
  text.textContent = 'You have work here from before you signed in. ';
  wrap.appendChild(text);

  const accept = document.createElement('button');
  accept.type = 'button';
  accept.className = 'tool-sync-offer-btn';
  accept.textContent = 'Bring it into my account';
  accept.addEventListener('click', async () => {
    // "Nothing saved on the account side" was true when the offer RENDERED; the person may have
    // used the tool since, and its autosave writes the account-scoped store. Importing over that
    // would destroy the newer work in favor of an old draft - so re-check at click time and
    // withdraw the offer instead, keeping what they just did.
    if (localStore.load()) {
      wrap.remove();
      return;
    }

    accept.disabled = true;
    localStore.save(draft);
    await push(slug, draft, schemaVersion, HYDRATE_TIMEOUT_MS).catch(() => {});
    // The anonymous copy is left exactly where it was: it may belong to someone else, and taking
    // a copy is not the same as taking it away.
    window.location.reload();
  });
  wrap.appendChild(accept);

  const dismiss = document.createElement('button');
  dismiss.type = 'button';
  dismiss.className = 'tool-sync-offer-btn tool-sync-offer-dismiss';
  dismiss.textContent = 'No thanks';
  dismiss.addEventListener('click', () => {
    declineStore(slug).save(true);
    // The button being removed is the one holding focus - hand it somewhere real, or it lands on
    // <body> and a keyboard user's place in the page silently evaporates.
    const nextFocus = host.querySelector('.tool-pin-btn') || document.querySelector('#main-content, main');
    wrap.remove();
    if (nextFocus && typeof nextFocus.focus === 'function') nextFocus.focus();
  });
  wrap.appendChild(dismiss);

  host.appendChild(wrap);
}
